import { ReactNode, useEffect, useMemo } from 'react';

import { FormDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import formsVector from '@/assets/vectors/forms.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { usePermissions } from '@/hooks/use-permissions';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createScheduleFormFormsTableColumns } from './table-meta';

export function ScheduleFormFormsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: FormDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) {
  const navigate = useWorkspaceNavigate();
  const { syncModuleColumns } = useModulesStore();
  const { canManageScheduleForms } = usePermissions();
  const columns = createScheduleFormFormsTableColumns({ canManageScheduleForms });

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.ScheduleFormForms, createScheduleFormFormsTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<FormDTO>
        data={data}
        columns={columns}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={(row) => navigate(`/schedules/forms/${row.id}/submissions`)}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoScheduleFormFormsCreatedDisplay({ addForm }: { addForm: () => void }) {
  const { canManageScheduleForms } = usePermissions();
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={formsVector} alt="Forms" className="h-[170px]" />
      <span className="text-xl leading-[30px] font-medium">No forms have been created yet</span>
      {canManageScheduleForms && (
        <>
          <div className="flex flex-col gap-0 text-center">
            <span className="text-secondary-text text-sm">
              Create custom schedule forms for your mechanics to fill out when performing asset services, checks and tests.
            </span>
          </div>
          <div className="pointer-events-auto flex gap-1">
            <Button variant="success" onClick={addForm}>
              Create Schedule Form
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function NoScheduleFormFormsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={formsVector} alt="Forms" className="h-[170px]" />
      <span className="text-sm">No forms match your search</span>
    </div>
  );
}

export function NoScheduleFormFormsFoundFromFiltersDisplay({ addForm, removeColumnFilters }: { addForm: () => void; removeColumnFilters: () => void }) {
  const { canManageScheduleForms } = usePermissions();
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={formsVector} alt="Forms" className="h-[170px]" />
      <span className="text-xl font-medium">No forms match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters{canManageScheduleForms ? ' or create a new schedule form' : ''}.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
        {canManageScheduleForms && (
          <Button variant="success" onClick={addForm}>
            Create Schedule Form
          </Button>
        )}
      </div>
    </div>
  );
}
